import { useEffect, useRef, useState } from "react";
import { Download, MessageSquare, Check, Pen, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { withUtm } from "@/components/thales/utm";
import OmbelineContactPopover from "@/components/thales/OmbelineContactPopover";
import { useArkemaLang } from "@/components/thales/i18n";
import { trackPostHogEvent } from "@/lib/analytics";

const ThalesHowTo = () => {
  const { t, lang } = useArkemaLang();
  const videoId = "1eqOjPk8Fto";

  const steps = [
    {
      n: 1,
      Icon: Send,
      title: t("how.s1.title"),
      description: t("how.s1.desc"),
      links: [
        { label: t("how.s1.link.platform"), href: "https://kiabi.naboo.app/" },
        { label: "celia.desailly@naboo.app", href: "mailto:celia.desailly@naboo.app" },
      ],
    },
    { n: 2, Icon: MessageSquare, title: t("how.s2.title"), description: t("how.s2.desc") },
    { n: 3, Icon: Pen, title: t("how.s3.title"), description: t("how.s3.desc") },
    { n: 4, Icon: Check, title: t("how.s4.title"), description: t("how.s4.desc") },
    { n: 5, Icon: Download, title: t("how.s5.title"), description: t("how.s5.desc") },
    { n: 6, Icon: MessageSquare, title: t("how.s6.title"), description: t("how.s6.desc") },
  ];
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const step = steps[current];

  const sectionRef = useRef<HTMLElement | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const videoTrackedRef = useRef(false);

  // Start autoplay when section enters viewport
  useEffect(() => {
    if (!sectionRef.current || hasStarted) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsAutoPlaying(true);
            setHasStarted(true);
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  // Auto-advance every 2.5s, looping continuously
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrent((c) => {
        const next = (c + 1) % steps.length;
        setDirection(next > c ? "forward" : "forward");
        return next;
      });
    }, 2500);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goTo = (i: number) => {
    if (i === current) return;
    setDirection(i > current ? "forward" : "backward");
    setCurrent(i);
    setIsAutoPlaying(false);
  };

  const goPrev = () => {
    setDirection("backward");
    setCurrent((c) => (c - 1 + steps.length) % steps.length);
    setIsAutoPlaying(false);
  };

  const goNext = () => {
    setDirection("forward");
    setCurrent((c) => (c + 1) % steps.length);
    setIsAutoPlaying(false);
  };

  return (
    <section ref={sectionRef} className="py-6 md:py-10 px-4 bg-white">
      <div className="max-w-[1126px] mx-auto">
        <div className="text-center mb-6 md:mb-8">
          <span className="inline-block bg-[#C6E278] text-[#111111] text-xs uppercase tracking-wider font-semibold px-3 py-1 rounded-full mb-4">
            {t("how.badge")}
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#111111]">
            {t("how.title")}
          </h2>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {steps.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`${t("how.go_to")} ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === current
                  ? "w-10 bg-[#111111]"
                  : i < current
                  ? "w-1.5 bg-[#C6E278]"
                  : "w-1.5 bg-black/15 hover:bg-black/30"
              }`}
            />
          ))}
        </div>

        {/* Card */}
        <div className="relative">
          <button
            type="button"
            onClick={goPrev}
            aria-label={t("how.prev")}
            className="absolute left-0 sm:-left-4 md:-left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white border border-black/10 shadow-md flex items-center justify-center text-[#111111] hover:bg-[#C6E278] hover:border-[#C6E278] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label={t("how.next")}
            className="absolute right-0 sm:-right-4 md:-right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white border border-black/10 shadow-md flex items-center justify-center text-[#111111] hover:bg-[#C6E278] hover:border-[#C6E278] transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div
            key={step.n}
            className={`relative bg-white border border-black/10 rounded-3xl p-6 sm:p-8 md:p-10 shadow-sm ${
              direction === "forward" ? "animate-slide-in-right" : "animate-slide-in-left"
            }`}
          >


            {/* Step number badge */}
            <div className="absolute -top-4 left-6 sm:left-8 md:left-10 inline-flex items-center gap-2 bg-[#111111] text-white text-xs font-semibold px-3 py-1.5 rounded-full">
              {t("how.step")} {step.n} / {steps.length}
            </div>

            <div className="relative flex flex-col sm:flex-row sm:items-start gap-5 px-2 sm:px-6">
              <div className="shrink-0 w-14 h-14 rounded-2xl bg-[#C6E278] flex items-center justify-center">
                <step.Icon className="w-7 h-7 text-[#111111]" />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-xl sm:text-2xl font-bold text-[#111111] leading-tight mb-2">
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base text-[#404c5d] leading-relaxed">
                  {step.description}
                </p>

                {step.links && (
                  <div className="relative z-20 mt-4 flex flex-wrap gap-x-5 gap-y-1.5">
                    {step.links.map((l) => {
                      const linkClass = "inline-flex items-center text-sm text-[#111111] hover:text-[#a8c45e] underline decoration-[#C6E278] decoration-2 underline-offset-4 font-medium transition-colors";
                      if (l.href.startsWith("mailto:")) {
                        return (
                          <OmbelineContactPopover key={l.href} align="start">
                            <button
                              type="button"
                              className={linkClass}
                              onClick={() =>
                                trackPostHogEvent("siemens_contact_celia_clicked", {
                                  page: "siemens",
                                  location: "how_to",
                                  method: "popover",
                                  lang,
                                })
                              }
                            >
                              <span className="mr-1.5">→</span>
                              {l.label}
                            </button>
                          </OmbelineContactPopover>
                        );
                      }
                      return (
                        <a
                          key={l.href}
                          href={withUtm(l.href)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={linkClass}
                        >
                          <span className="mr-1.5">→</span>
                          {l.label}
                        </a>
                      );
                    })}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>


        {/* Video */}
        <div className="mt-6 md:mt-8">
          <div
            className="relative aspect-video w-full rounded-2xl overflow-hidden shadow-lg"
            onClick={() => {
              if (videoTrackedRef.current) return;
              videoTrackedRef.current = true;
              trackPostHogEvent("siemens_video_played", {
                page: "siemens",
                video_id: videoId,
                lang,
              });
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              className="absolute inset-0 w-full h-full"
              title="Naboo — Comment ça marche"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThalesHowTo;
